// Maquette de la coquille (decision de Louka du 01/10) : la page de l'app dans le rectangle du chat.
// Le cadre naît à la première visite d'une page de l'app puis reste en vie, caché sur les pages de
// Dust : revenir à Documents ne recharge pas l'app. L'app y annonce la page qu'elle affiche (le chat
// met son adresse à jour, sans entrée d'historique) ; le chat lui demande d'en changer par message,
// sans recharger le cadre. Monté par AgentSurfaceRouterLayout (front-spa), seulement quand le modèle
// de navigation de l'app le demande (`coquille`).

import {
  cheminDeLApp,
  messageAller,
  pageDuMessage,
  routeDeLaPageDeLApp,
} from "@app/lib/compta/coquille";
import { useNavigationDeLApp } from "@app/lib/compta/navigation_app";
import { useAppRouter } from "@app/lib/platform";
import { getConversationRoute } from "@app/lib/utils/router";
import type { LightWorkspaceType } from "@app/types/user";
import { cn } from "@dust-tt/sparkle";
import { useCallback, useEffect, useRef, useState } from "react";

const ROUTE_DE_LA_PAGE = /^\/w\/[^/]+\/app$/;

export function CadreDeLApp({
  chemin,
  visible,
  surPage,
  surAssistant,
}: {
  /** La page de l'app demandée par l'adresse du chat, ou null hors de cette page. */
  chemin: string | null;
  visible: boolean;
  /** L'app a changé de page d'elle-même (lien, retour) : le chat suit. */
  surPage: (chemin: string) => void;
  /** L'app voulait ouvrir sa page Assistant : le chat ouvre une conversation à la place. */
  surAssistant: () => void;
}) {
  const cadre = useRef<HTMLIFrameElement>(null);
  const [depart, setDepart] = useState<string | null>(chemin);
  const departRef = useRef(depart);
  const pret = useRef(false);
  const interieur = useRef<string | null>(null);
  const voulu = useRef<string | null>(chemin);
  const etat = useRef({ visible, surPage, surAssistant });

  useEffect(() => {
    etat.current = { visible, surPage, surAssistant };
  }, [visible, surPage, surAssistant]);

  useEffect(() => {
    if (depart === null && chemin !== null) {
      departRef.current = chemin;
      setDepart(chemin);
    }
  }, [chemin, depart]);

  const aller = useCallback((c: string) => {
    try {
      cadre.current?.contentWindow?.postMessage(
        messageAller(c),
        window.location.origin
      );
    } catch {
      // Cadre sur une page d'erreur : rien à envoyer.
    }
  }, []);

  useEffect(() => {
    if (chemin === null) {
      return;
    }
    voulu.current = chemin;
    if (pret.current && chemin !== interieur.current) {
      aller(chemin);
    }
  }, [chemin, aller]);

  useEffect(() => {
    const surMessage = (event: MessageEvent) => {
      const p = pageDuMessage(
        event,
        cadre.current?.contentWindow,
        window.location.origin
      );
      if (p === null) {
        return;
      }
      if (cheminDeLApp(p) === null) {
        if (etat.current.visible && p.split("?")[0] === "/assistant") {
          etat.current.surAssistant();
        }
        return;
      }
      interieur.current = p;
      if (!pret.current) {
        pret.current = true;
        // Une autre page a été demandée pendant le chargement : elle part maintenant, et la page de
        // départ annoncée en retard ne ramène pas le chat en arrière.
        if (voulu.current !== null && voulu.current !== departRef.current) {
          aller(voulu.current);
          return;
        }
      }
      if (etat.current.visible && p !== voulu.current) {
        etat.current.surPage(p);
      }
    };
    window.addEventListener("message", surMessage);
    return () => window.removeEventListener("message", surMessage);
  }, [aller]);

  if (depart === null) {
    return null;
  }
  return (
    <div
      hidden={!visible}
      // Sur telephone, le layout de Dust laisse defiler le document (hauteurs libres) : le cadre prend
      // l'ecran moins la barre du haut (h-title, 3rem) ; sur ordinateur, il remplit le rectangle.
      className={cn(
        "h-[calc(100dvh-3rem)] min-h-0 flex-none flex-col md:h-auto md:flex-1",
        visible ? "flex" : "hidden"
      )}
    >
      <iframe
        ref={cadre}
        src={depart}
        title="Page de l'application"
        className="min-h-0 w-full flex-1 border-0"
      />
    </div>
  );
}

/** Le cadre branché sur l'adresse du chat : /w/<espace>/app?chemin=<page de l'app>. */
export function CadreDeLAppDuChat({ owner }: { owner: LightWorkspaceType }) {
  const router = useAppRouter();
  const nav = useNavigationDeLApp();
  const surLaPage = ROUTE_DE_LA_PAGE.test(router.asPath.split("?")[0]);
  const brut = router.query.chemin;
  const chemin = surLaPage
    ? cheminDeLApp(typeof brut === "string" ? brut : undefined)
    : null;
  const { replace, push } = router;
  const surPage = useCallback(
    (c: string) => {
      void replace(routeDeLaPageDeLApp(owner.sId, c));
    },
    [replace, owner.sId]
  );
  const surAssistant = useCallback(() => {
    void push(getConversationRoute(owner.sId));
  }, [push, owner.sId]);
  if (!nav?.coquille) {
    return null;
  }
  return (
    <CadreDeLApp
      chemin={chemin}
      visible={chemin !== null}
      surPage={surPage}
      surAssistant={surAssistant}
    />
  );
}
